/**
 * The Long Night's pixel palette (docs/LONG_NIGHT_ART_DIRECTION.md §12.13): every sprite is drawn
 * in one material, four tones of it lit the same way (a highlight where the lamp catches the top
 * edge, a light, a body and a shadow that leans toward violet), inside a deep violet outline.
 * Sprites store tones, not colors, so the same sprite can be shown lit or as a silhouette.
 */

export type Mat =
  | 'iron'
  | 'steel'
  | 'brass'
  | 'gold'
  | 'silver'
  | 'copper'
  | 'paper'
  | 'bone'
  | 'wood'
  | 'leather'
  | 'wax'
  | 'red'
  | 'blue'
  | 'green'
  | 'yellow'
  | 'rose'
  | 'azure'
  | 'glass'
  | 'cloth'
  | 'velvet'
  | 'black'
  | 'ink'
  | 'stone'
  | 'white'
  | 'flame'
  | 'spectral'
  | 'straw'
  | 'pearl'
  | 'earth'
  | 'fur'
  | 'pink'
  | 'oxblood'
  | 'verdigris';

/** Highlight, light, body and shadow of each material. */
export const PIXEL_MATS: Readonly<Record<Mat, readonly [string, string, string, string]>> = {
  iron: ['#e4e2ee', '#b4b0c4', '#7c7894', '#4a4462'],
  steel: ['#f2f6ff', '#c2cadc', '#8890ac', '#4e5274'],
  brass: ['#fff0b8', '#e8c070', '#b8863c', '#6e4a2a'],
  gold: ['#fff7c0', '#ffd44a', '#e09a1c', '#8a4e1a'],
  silver: ['#ffffff', '#dde2ee', '#a8aec4', '#666a8a'],
  copper: ['#ffd0a8', '#e8925a', '#b05a34', '#64302a'],
  paper: ['#fffdf4', '#f0e4c8', '#c8b28c', '#86705e'],
  bone: ['#fffcf0', '#ece0c4', '#bca888', '#7a6656'],
  wood: ['#e8b078', '#b8784a', '#83492e', '#4c2a24'],
  leather: ['#c8905e', '#94603c', '#663c2c', '#3a2224'],
  wax: ['#ff7a8a', '#d43a50', '#8e1a36', '#4e0c26'],
  red: ['#ff9a84', '#ee4a3a', '#a82434', '#5e1230'],
  blue: ['#b4d4ff', '#5a8cf0', '#3450b4', '#1e2468'],
  green: ['#b0f0c4', '#4cc884', '#22885e', '#124048'],
  yellow: ['#fff4a0', '#ffd23a', '#d0901e', '#7e4a1e'],
  rose: ['#ffd0ea', '#f46aaa', '#b42c78', '#5e1452'],
  azure: ['#e8f8ff', '#7ccaff', '#3a78e0', '#1e3486'],
  glass: ['#f0fcff', '#a4dcf4', '#5a98cc', '#2e4c86'],
  cloth: ['#d0a4ea', '#9a64c0', '#643c8c', '#341e56'],
  velvet: ['#d4648c', '#a0305c', '#64183c', '#320c26'],
  black: ['#a090c0', '#625280', '#3a2e56', '#1e1630'],
  ink: ['#9a8ac4', '#5e4e88', '#382a5c', '#1c1236'],
  stone: ['#d4d0de', '#a09cb2', '#6c6880', '#3e3a52'],
  white: ['#ffffff', '#f0eef8', '#c4c0d6', '#7e7a98'],
  flame: ['#fffbe0', '#ffe080', '#ff9a3a', '#c8461e'],
  spectral: ['#e0fff4', '#86f4cc', '#36b0a0', '#1e5a66'],
  straw: ['#fff0b0', '#e6c870', '#b08a3e', '#6a4c24'],
  pearl: ['#ffffff', '#f4ecfa', '#cabadc', '#8a78a6'],
  earth: ['#b48a64', '#80583e', '#523628', '#2c1c1e'],
  fur: ['#fffaf0', '#ece0cc', '#bcaa94', '#7a6a66'],
  pink: ['#ffe4ee', '#f8a6c0', '#cc6a8e', '#7c3a5e'],
  oxblood: ['#e05a6e', '#a8304a', '#6a1a32', '#38101e'],
  verdigris: ['#c6f0e2', '#7cc4ae', '#3e8a7e', '#1e4a50'],
};

/** Every sprite's outline: the void, a little violet. */
export const PIXEL_OUTLINE = '#140c22';

/** A thing not yet found: an ink shape with a cold rim where its outline was. */
export const SILHOUETTE = { fill: '#0a0810', rim: '#3a2f5a' } as const;

/**
 * Tones a sprite stores, one character per pixel: 0 nothing, 1 outline, 2 highlight, 3 light,
 * 4 body, 5 shadow.
 */
export type Tone = 0 | 1 | 2 | 3 | 4 | 5;

/** A sprite's colors by tone, lit or as a silhouette. */
export function toneColors(mat: Mat, silhouette = false): readonly string[] {
  if (silhouette) {
    const f = SILHOUETTE.fill;
    return ['', SILHOUETTE.rim, f, f, f, f];
  }
  return ['', PIXEL_OUTLINE, ...PIXEL_MATS[mat]];
}

/**
 * A sprite's pixels as SVG paths, one per color index (a base-36 digit per pixel, 0 for none):
 * each row's runs of a color as one rectangle. `grid` holds `w` columns per row.
 */
export function spritePaths(grid: string, w: number): readonly (readonly [number, string])[] {
  const d = new Map<number, string>();
  const h = grid.length / w;
  for (let y = 0; y < h; y++) {
    let x = 0;
    while (x < w) {
      const ch = grid[y * w + x]!;
      let run = 1;
      while (x + run < w && grid[y * w + x + run] === ch) run++;
      const c = parseInt(ch, 36);
      if (c > 0) d.set(c, `${d.get(c) ?? ''}M${x} ${y}h${run}v1h${-run}z`);
      x += run;
    }
  }
  return [...d].sort((a, b) => a[0] - b[0]);
}

/** Expands a run-length string ("<color><run in base 36>…") into one character per pixel. */
export function unpackRuns(runs: string): string {
  let out = '';
  for (let i = 0; i < runs.length; i += 2) out += runs[i]!.repeat(parseInt(runs[i + 1]!, 36));
  return out;
}
